import { Skeleton } from '@/components/skeleton';
import { cn, memoClassNames } from '@/utils';
import { Slot } from '@radix-ui/react-slot';
import {
  useDashboardIndicator,
  UseDashboardIndicatorProps,
} from './hooks/use-dashboard-indicator';
import { StatIndicator } from './types';

const classes = memoClassNames('DashboardBlock', ['root', 'loader']);

export type DashboardBlockProps = React.ComponentProps<'div'> & {
  type: string;
  size?: number;
  loading?: boolean;
  asChild?: boolean;
  slots?: {
    Loading?: React.ElementType;
  };
};

export const DashboardBlock = ({
  type,
  size = 60,
  asChild,
  className,
  loading,
  slots = {},
  children,
  ...others
}: DashboardBlockProps) => {
  const Comp = asChild ? Slot : 'div';
  const { Loading = Skeleton } = slots;

  return (
    <Comp
      {...others}
      className={cn(classes.root, 'mm:grow', className)}
      style={{
        flexBasis: `calc(var(--mm-spacing) * ${size})`,
        ...others.style,
      }}
      data-type={type}
      data-loading={loading || undefined}
    >
      {loading ? (
        <Loading className={`${classes.loader} mm:size-full mm:min-h-16`} />
      ) : (
        children
      )}
    </Comp>
  );
};

DashboardBlock.displayName = 'DashboardBlock';

export type DashboardIndicatorBlockProps = React.ComponentProps<'div'> &
  UseDashboardIndicatorProps & {
    type?: string;
    render?: (props: {
      label: React.ReactNode;
      description: React.ReactNode;
      value: React.ReactNode;
      refreshing: boolean;
      data: StatIndicator | null;
    }) => React.ReactNode;
  };

export const DashboardIndicatorBlock = ({
  select,
  type,
  displayDescription,
  displayLabel,
  displayValue,
  render = () => null,
  ...others
}: DashboardIndicatorBlockProps) => {
  const { display, loading, current } = useDashboardIndicator({
    select,
    displayDescription,
    displayLabel,
    displayValue,
  });

  const refreshing = current && loading;

  return (
    <DashboardBlock
      {...others}
      loading={loading && !refreshing}
      type={type || 'indicator'}
      data-metric={current?.metric}
      data-indicator={current?.type}
      data-refreshing={refreshing}
    >
      {render({
        label: null,
        description: null,
        value: null,
        refreshing: Boolean(refreshing),
        data: current || null,
        ...display,
      })}
    </DashboardBlock>
  );
};
